import { useCallback, useEffect, useState } from "react";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { initialFiltersValues, useCentroCustoListaFilters } from "../hooks/centro_custo_lista/filters";
import { useCentroCustoListaPagination } from "../hooks/centro_custo_lista/pagination";
import { CentroCustoLista } from "../../domain/models/centro_custo_lista";
import { processError } from "../../../../common/utils/base_error";
import { useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { DropDown } from "../../../../common/domain/models/dropdown";

type LoadCentroCustoListaState = {
    codigo?: string;
    idsCentroCusto?: number[];
    status?: number;
    page?: number;
    rowsPerPage?: number;
}

export const useCentroCustoListaViewModel = () => {
    const {
        commons: {
            getCentroCustoDropDownUseCase,

        },
        comercial: {
            getAllCentroCustoListaUseCase,
            deleteCentroCustoByIdUseCase,
        },
    } = useDependencyInjection();

    const navigate = useNavigate();

    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    /** Estado responsável para definir um delay para a ativação de uma função.
     * Parametro: tempo de delay em milisegundos.
     */

    const debouncedRef = useCreateDebounceHandler(750);
    /**
     * Estados relacionados aos modais de cadastro e de deleção de centro custo.
     */
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const [selectedIdCentroCusto, setSelectedIdCentroCusto] = useState<number>();
    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();

    /**
     * Usado para o processo de carregamento.
     */
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    /**
     * Estado de erro que impede o carregamento da página.
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Estado de erro que ocorre após o carregamento da página.
     */
    const [centroCustoLista, setCentroCustoLista] = useState<CentroCustoLista[]>()

    /**
     * Estados globais da aplicação criados pelo zustand.
     * Filtros: Armazena os estados e funções dos campos de filtro da pesquisa.
     * Pagination: Armazena os estados e funções de páginação da tabela.
     */

    const filters = useCentroCustoListaFilters();
    const pagination = useCentroCustoListaPagination();


    /**
     * Funções de carregamento
     */


    //#region CARREGAMENTO

    const loadCentroCustoListaState = useCallback(async (funcState?: LoadCentroCustoListaState) => {
        try {
            const filteredCodigo = funcState?.codigo ?? filters.codigo;
            const filteredNomeCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredStatus = funcState?.status ?? filters.status;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllCentroCustoListaUseCase.getAllCentroCustoLista(
                filteredCodigo,
                filteredNomeCentroCusto,
                filteredStatus,
                filteredPage,
                filteredRowsPerPage,
            );

            setCentroCustoLista(getAllCentroCustoListaUseCase.items);

            pagination.setRowsPerPage(getAllCentroCustoListaUseCase.pageSize);
            pagination.setTotalItems(getAllCentroCustoListaUseCase.totalItems);
        } catch (e: unknown) {
            setCentroCustoLista([]);
            throw new Error("Falha ao buscar motivos de afastamento!");
        }
    }, [
        filters,
        pagination,
        getAllCentroCustoListaUseCase,
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase]);



    //#endregion
    /**
     * Função otimizada e eficiente para controle de estado dos dados.
     * Função responsável em controlar a atualização dos dados sempre mantendo em 
     * atualizados.
     * @param funcState model criado para consulta da lista de centro de custos.
     */


    /**
     * useEffect
     * Apenas carregamento inicial dos estados.
     */

    useEffect(() => {
        (async function () {
            if (!loading && !errorMessage) {
                try {
                    setLoading(true);
                    setErrorMessage(undefined);

                    if (centroCustoLista === undefined) {
                        await loadCentroCustoDropDown()
                        await loadCentroCustoListaState();

                    }
                } catch (e: unknown) {
                    setErrorMessage(processError(e, "Falha ao buscar lista de centro de custo!")[0]);
                } finally {
                    setLoading(false);
                }
            }
        })();
    }, [
        loading,
        errorMessage,
        centroCustoLista,
        loadCentroCustoListaState,
    ]);


    //#region TABELA

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadCentroCustoListaState();
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar lista de centro custo!'));
        } finally {
            setLoading(false);
        }
    }, [
        loadCentroCustoListaState,
        pushErrorNotification,
    ]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);

            pagination.setPage(page + 1);

            await loadCentroCustoListaState({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar lista de centro de custo!'));
        } finally {
            setLoading(false);
        }
    }, [
        pagination,
        loadCentroCustoListaState,
        pushErrorNotification,
    ]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);

            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);

            await loadCentroCustoListaState({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar motivos de afastamento!'));
        } finally {
            setLoading(false);
        }
    }, [
        pagination,
        loadCentroCustoListaState,
        pushErrorNotification,
    ]);

    //#endregion






    //#region PESQUISA


    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
        filters.setStatus(status);
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadCentroCustoListaState({ status, page: 1 });
            } catch (e: unknown) {
                pushErrorNotification('Erro', processError(e, 'Falha ao buscar motivos de afastamento!'));
            } finally {
                setLoading(false);
            }
        };
    }, [filters, loadCentroCustoListaState]);

    const handleCentroCustoFilterChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
        filters.setIdsCentroCusto(newValue);

        return async () => {
            setLoading(true);
            pagination.setPage(1);
            await loadCentroCustoListaState({ idsCentroCusto: newValue, page: 1 })
        }
    }, [loadCentroCustoListaState, filters]);


    const handleCodigoFilterChange = useDebouncedCallback(debouncedRef, (codigo: string) => {
        filters.setCodigo(codigo);

        return async () => {
            setLoading(true);
            pagination.setPage(1);
            await loadCentroCustoListaState({ codigo, page: 1 })
        }
    }, [loadCentroCustoListaState, filters]);

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadCentroCustoListaState({ ...initialFiltersValues, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar cargos!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadCentroCustoListaState,
        pushErrorNotification,
    ]);

    //#endregion



    //#region DELEÇÃO
    const handleCentroCustoDeleteOpenState = useCallback((idCentroCusto: number | null, openState: boolean) => {
        if (openState) {
            if (idCentroCusto && centroCustoLista) {
                setSelectedIdCentroCusto(centroCustoLista.find(x => x.idCentroCusto === idCentroCusto)?.idCentroCusto);
            } else {
                setSelectedIdCentroCusto(undefined);
            }
        }
        setOpenDeleteModal(openState);
    }, [centroCustoLista]);


    const handleConfirmarDeletarCentroCusto = useCallback(async () => {
        try {
            if (selectedIdCentroCusto) {
                setLoading(true);

                const errors = await deleteCentroCustoByIdUseCase.deleteCentroCustoById(selectedIdCentroCusto);
                if (errors.success) {
                    pushInfoNotification("SUCESSO", "Centro de custo deletado com sucesso !!", { isAutoFinish: true })
                } else {
                    if (errors.listaErros !== undefined && errors.listaErros.length > 0) {
                        errors.listaErros.forEach((erro, index) => {
                            pushErrorNotification('Falha', [erro], { isAutoFinish: true })
                        })
                    }
                }
            };
            await loadCentroCustoListaState();

        } catch (e: unknown) {
            pushErrorNotification('Falha', processError(e, 'Falha ao deletar Centro Custo da lista!'), { isAutoFinish: true })
        } finally {
            setLoading(false);
            setOpenDeleteModal(false);
        }
    }, [
        selectedIdCentroCusto,
        deleteCentroCustoByIdUseCase,
        loadCentroCustoListaState,
        pushInfoNotification,
        pushErrorNotification,
    ]);
    //#endregion





    //#region EDIÇÃO E NOVO ITEM

    const handleCentroCustoEdit = useCallback((idCentroCusto: number | null) => {
        navigate('/CentroCusto_cadastro', { state: { idCentroCusto } })
    }, [navigate]);

    const handleNewCentroCusto = useCallback(() => {
        navigate("/CentroCusto_cadastro");
    }, [navigate]);

    //#endregion


    return {
        loading,
        saving,
        centroCustoLista: centroCustoLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlCentroCusto,

        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters,
        handleCentroCustoEdit,
        handleCentroCustoDeleteOpenState,
        handleNewCentroCusto,
        handleConfirmarDeletarCentroCusto,
        handleCentroCustoFilterChange,
        handleCodigoFilterChange,

    };
}

